import { useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBillingCycles, useUnitCompletions, useUnitCompletionDiscussions } from "@/hooks/useUnitSchedule";
import { useContractSovLines, useProjectContracts, useWorkOrderLines } from "@/hooks/useProjectContracts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CheckCircle2, XCircle, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { generateBillNumber } from "@/lib/naming";
import { format } from "date-fns";
import { createBillApproval } from "@/lib/createBillApproval";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtQty = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

interface Props {
  projectId: string;
  project: any;
  onMutate: () => void;
}

export const UnitCompletionReview = ({ projectId, project, onMutate }: Props) => {
  const { data: cycles } = useBillingCycles(projectId);
  const [selectedCycleId, setSelectedCycleId] = useState<string>("");
  const { data: completions, refetch: refetchCompletions } = useUnitCompletions(projectId, selectedCycleId || undefined);
  const { data: contracts } = useProjectContracts(projectId);
  const contract = contracts?.[0];
  const { data: sovLines } = useContractSovLines(contract?.id);
  const { data: woLines } = useWorkOrderLines(projectId);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectItem, setRejectItem] = useState<any>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectCategory, setRejectCategory] = useState("not_completed");
  const [busy, setBusy] = useState(false);
  const [discussionOpen, setDiscussionOpen] = useState(false);
  const [discussionItem, setDiscussionItem] = useState<any>(null);
  const [newMessage, setNewMessage] = useState("");

  // Auto-select latest cycle
  if (!selectedCycleId && cycles?.length) {
    const latest = cycles[0];
    setSelectedCycleId(latest.id);
  }

  const submitted = (completions ?? []).filter((c: any) => c.status === "submitted");
  const approved = (completions ?? []).filter((c: any) => c.status === "approved");
  const rejected = (completions ?? []).filter((c: any) => c.status === "rejected");

  // Calculate amounts by looking up WO line rates
  const getRate = (sovLineId: string, woId: string) => {
    const wol = (woLines ?? []).find((l: any) => l.sov_line_id === sovLineId && l.work_order_id === woId);
    return wol ? Number(wol.contract_rate) : 0;
  };

  const submittedTotal = submitted.reduce((s: number, c: any) => {
    return s + Number(c.quantity) * getRate(c.sov_line_id, c.work_order_id);
  }, 0);

  const handleApprove = async (completionId: string) => {
    const { error } = await supabase
      .from("unit_completions")
      .update({ status: "approved", reviewed_at: new Date().toISOString(), reviewed_by: (await supabase.auth.getUser()).data.user?.id } as any)
      .eq("id", completionId);
    if (error) toast.error(error.message);
    else {
      toast.success("Item approved");
      refetchCompletions();
    }
  };

  const handleApproveAll = async () => {
    setBusy(true);
    const userId = (await supabase.auth.getUser()).data.user?.id;
    for (const c of submitted) {
      await supabase
        .from("unit_completions")
        .update({ status: "approved", reviewed_at: new Date().toISOString(), reviewed_by: userId } as any)
        .eq("id", c.id);
    }
    toast.success(`${submitted.length} items approved`);
    refetchCompletions();
    setBusy(false);
  };

  const openReject = (item: any) => {
    setRejectItem(item);
    setRejectReason("");
    setRejectCategory("not_completed");
    setRejectOpen(true);
  };

  const handleReject = async () => {
    if (!rejectItem || !rejectReason.trim()) return;
    setBusy(true);
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const { error } = await supabase
      .from("unit_completions")
      .update({
        status: "rejected",
        reviewed_at: new Date().toISOString(),
        reviewed_by: userId,
        rejection_reason: `[${rejectCategory}] ${rejectReason.trim()}`,
      } as any)
      .eq("id", rejectItem.id);

    if (error) { toast.error(error.message); setBusy(false); return; }

    // Create discussion thread
    const { data: companyId } = await supabase.rpc("my_company_id");
    await supabase.from("unit_completion_discussions").insert({
      company_id: companyId,
      unit_completion_id: rejectItem.id,
      project_id: projectId,
      message: rejectReason.trim(),
      sent_by: userId,
      sender_type: "field_pm",
      sender_name: "Field PM",
    } as any);

    toast.success("Item rejected — discussion thread opened");
    setRejectOpen(false);
    refetchCompletions();
    setBusy(false);
  };

  const handleConvertToBill = async () => {
    if (!approved.length) return;
    setBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      const cycle = cycles?.find((c: any) => c.id === selectedCycleId);

      const total = approved.reduce((s: number, c: any) => {
        return s + Number(c.quantity) * getRate(c.sov_line_id, c.work_order_id);
      }, 0);

      const retainagePct = Number(project?.default_retainage_pct ?? 10);
      const retainage = total * retainagePct / 100;
      const netTotal = total - retainage;

      // Create bill
      const { data: bill, error: billErr } = await supabase
        .from("bills")
        .insert({
          company_id: companyId,
          project_id: projectId,
          bill_number: `BILL-${cycle?.cycle_name ?? "CYCLE"}-${(await supabase.from("bills").select("id", { count: "exact", head: true }).eq("project_id", projectId).eq("billing_cycle_id", selectedCycleId)).count ?? 0 + 1}`,
          bill_date: new Date().toISOString().split("T")[0],
          vendor_id: approved[0]?.vendor_id,
          billing_cycle_id: selectedCycleId,
          subtotal: total,
          retainage_percent: retainagePct,
          retainage_amount: retainage,
          total: netTotal,
          status: "draft",
          locked_at: new Date().toISOString(),
          locked_by: (await supabase.auth.getUser()).data.user?.id,
          converted_by: (await supabase.auth.getUser()).data.user?.id,
        } as any)
        .select("id")
        .single();

      if (billErr) throw billErr;

      // Create approval request
      const userId = (await supabase.auth.getUser()).data.user?.id;
      await createBillApproval({
        companyId: companyId!,
        requestType: "invoice_approve",
        title: `Bill ${cycle?.cycle_name ?? "CYCLE"} — ${approved.length} items`,
        projectId,
        linkedBillId: bill.id,
        requestData: { subtotal: total, retainage, total: netTotal, items_count: approved.length },
        createdBy: userId,
      });

      // Create bill lines
      const billLines = approved.map((c: any) => {
        const rate = getRate(c.sov_line_id, c.work_order_id);
        const wolMatch = (woLines ?? []).find((l: any) => l.sov_line_id === c.sov_line_id && l.work_order_id === c.work_order_id && l.unit_id === c.unit_id);
        return {
          company_id: companyId,
          bill_id: bill.id,
          description: `Unit ${c.unit_schedule?.unit_number ?? "?"} — ${sovLines?.find((sl: any) => sl.id === c.sov_line_id)?.description ?? "Scope"}`,
          quantity: Number(c.quantity),
          unit_price: rate,
          line_total: Number(c.quantity) * rate,
          sov_line_id: c.sov_line_id,
          work_order_line_id: wolMatch?.id ?? null,
        };
      });

      await supabase.from("bill_lines").insert(billLines as any);

      // Mark completions as billed
      for (const c of approved) {
        await supabase
          .from("unit_completions")
          .update({ status: "billed", bill_id: bill.id } as any)
          .eq("id", c.id);
      }

      // Close billing cycle
      await supabase
        .from("billing_cycles")
        .update({ status: "finalized", finalized_at: new Date().toISOString(), finalized_by: (await supabase.auth.getUser()).data.user?.id } as any)
        .eq("id", selectedCycleId);

      toast.success(`Bill created — ${approved.length} items | Net: ${fmt(netTotal)}`);
      refetchCompletions();
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!cycles?.length) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">No billing cycles yet. Create a billing cycle to start reviewing completions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cycle Selector */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium">Billing Cycle</label>
        <Select value={selectedCycleId} onValueChange={setSelectedCycleId}>
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="Select cycle" />
          </SelectTrigger>
          <SelectContent>
            {cycles.map((c: any) => (
              <SelectItem key={c.id} value={c.id}>
                {c.cycle_name} — Cutoff: {c.cutoff_date}
                {c.status !== "open" && <Badge variant="outline" className="ml-2 text-xs">{c.status}</Badge>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Submitted Items */}
      {submitted.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>Submitted for Review ({submitted.length})</span>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleApproveAll} disabled={busy}>
                  <CheckCircle2 className="mr-1 h-4 w-4" /> Approve All
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Unit</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submitted.map((c: any) => {
                  const rate = getRate(c.sov_line_id, c.work_order_id);
                  const amount = Number(c.quantity) * rate;
                  const slName = sovLines?.find((sl: any) => sl.id === c.sov_line_id)?.description ?? "—";
                  return (
                    <TableRow key={c.id}>
                      <TableCell className="font-mono text-sm">Unit {c.unit_schedule?.unit_number ?? "?"}</TableCell>
                      <TableCell className="text-sm">{slName}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtQty(Number(c.quantity))}</TableCell>
                      <TableCell className="text-right text-sm">{fmt(rate)}</TableCell>
                      <TableCell className="text-right font-medium text-sm">{fmt(amount)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={() => handleApprove(c.id)}>
                            <CheckCircle2 className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => openReject(c)}>
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="flex justify-end gap-4 mt-3 text-sm">
              <span>Subtotal: <strong>{fmt(submittedTotal)}</strong></span>
              <span>Retainage ({project?.default_retainage_pct ?? 10}%): <strong>-{fmt(submittedTotal * (project?.default_retainage_pct ?? 10) / 100)}</strong></span>
              <span>Net: <strong className="text-primary">{fmt(submittedTotal * (1 - (project?.default_retainage_pct ?? 10) / 100))}</strong></span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approved items ready to convert */}
      {approved.length > 0 && (
        <Card className="border-primary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="text-primary">✅ Approved — Ready to Convert ({approved.length})</span>
              <Button size="sm" onClick={handleConvertToBill} disabled={busy}>
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Convert to Bill →
              </Button>
            </CardTitle>
          </CardHeader>
        </Card>
      )}

      {/* Rejected items */}
      {rejected.length > 0 && (
        <Card className="border-destructive/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-destructive">
              ❌ Rejected ({rejected.length}) — Available next cycle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rejected.map((c: any) => (
                <div key={c.id} className="flex items-center justify-between text-sm border-b pb-2">
                  <div>
                    <span className="font-mono">Unit {c.unit_schedule?.unit_number ?? "?"}</span>
                    <span className="text-muted-foreground ml-2">{sovLines?.find((sl: any) => sl.id === c.sov_line_id)?.description ?? ""}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground truncate max-w-[200px]">{c.rejection_reason}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setDiscussionItem(c); setDiscussionOpen(true); }}
                    >
                      <MessageSquare className="h-4 w-4 mr-1" /> Discussion
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {submitted.length === 0 && approved.length === 0 && rejected.length === 0 && (
        <p className="text-center text-muted-foreground py-8">No completions submitted for this billing cycle.</p>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject: Unit {rejectItem?.unit_schedule?.unit_number} — {sovLines?.find((sl: any) => sl.id === rejectItem?.sov_line_id)?.description}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <RadioGroup value={rejectCategory} onValueChange={setRejectCategory}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="not_completed" id="rc1" />
                <Label htmlFor="rc1">Work not completed — failed inspection</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="quantity_dispute" id="rc2" />
                <Label htmlFor="rc2">Quantity dispute</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="wrong_scope" id="rc3" />
                <Label htmlFor="rc3">Wrong scope item</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="other" id="rc4" />
                <Label htmlFor="rc4">Other</Label>
              </div>
            </RadioGroup>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (required)"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              This item will be locked for this cycle. Subcontractor can resubmit next billing cycle.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={busy || !rejectReason.trim()}>
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Reject & Open Discussion →
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discussion Thread Dialog */}
      <DiscussionThread
        open={discussionOpen}
        onOpenChange={setDiscussionOpen}
        completionItem={discussionItem}
        projectId={projectId}
        sovLines={sovLines ?? []}
      />
    </div>
  );
};

// Discussion Thread Sub-Component
function DiscussionThread({ open, onOpenChange, completionItem, projectId, sovLines }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  completionItem: any;
  projectId: string;
  sovLines: any[];
}) {
  const { data: messages, refetch } = useUnitCompletionDiscussions(completionItem?.id);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!newMessage.trim() || !completionItem) return;
    setSending(true);
    const { data: companyId } = await supabase.rpc("my_company_id");
    const user = (await supabase.auth.getUser()).data.user;
    const { error } = await supabase.from("unit_completion_discussions").insert({
      company_id: companyId,
      unit_completion_id: completionItem.id,
      project_id: projectId,
      message: newMessage.trim(),
      sent_by: user?.id,
      sender_type: "field_pm",
      sender_name: user?.email ?? "PM",
    } as any);
    if (error) toast.error(error.message);
    else {
      setNewMessage("");
      refetch();
    }
    setSending(false);
  };

  const slName = sovLines.find((sl: any) => sl.id === completionItem?.sov_line_id)?.description ?? "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">
            Unit {completionItem?.unit_schedule?.unit_number ?? "?"} — {slName}
            <Badge variant="destructive" className="ml-2">Rejected</Badge>
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[300px]">
          <div className="space-y-3">
            {(messages ?? []).map((m: any) => (
              <div key={m.id} className="text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{m.sender_name}</span>
                  <Badge variant="outline" className="text-[10px]">{m.sender_type}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {m.created_at ? format(new Date(m.created_at), "MMM d, h:mm a") : ""}
                  </span>
                </div>
                <p className="text-muted-foreground mt-0.5">{m.message}</p>
              </div>
            ))}
          </div>
        </ScrollArea>
        <div className="flex gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            rows={2}
            className="flex-1"
          />
          <Button onClick={handleSend} disabled={sending || !newMessage.trim()} size="sm">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
